import React, { useCallback, useMemo, useState } from 'react';

import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import type { ClaimStageConfiguration } from '~/components/ClaimStages/types';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import EditableChip from '~/components/core/Molecules/EditableChip';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { updateClaimStage } from './apiService';

type ClaimStageViewAndUpdateProps = {
  claimId: number;
  stageKey?: string | null;
  onUpdate?: (stageKey: string) => void | Promise<void>;
  viewOnly?: boolean;
};

const ClaimStageOptionDescription = ({ config }: { config: ClaimStageConfiguration }) => (
  <div className="flex items-center gap-8">
    <div className="group-[.claim-stage--selected]:hidden">
      <ColoredDot color={config.color} size="XS" />
    </div>
    {config.label}
  </div>
);

const ClaimStageViewAndUpdate = ({
  claimId,
  stageKey: initialStageKey,
  onUpdate,
  viewOnly = false,
}: ClaimStageViewAndUpdateProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentStageKey, setCurrentStageKey] = useState(initialStageKey);
  const { claimStageConfigurations } = useClaimStageConfigurations();
  const claimStageOptions = useMemo(
    () =>
      claimStageConfigurations?.map((config) => ({
        id: config.key,
        description: <ClaimStageOptionDescription config={config} />,
        color: config.color,
        disabled: !config.is_enabled,
      })),
    [claimStageConfigurations]
  );

  const handleStageChange = useCallback(
    async (stageKey: string) => {
      let fallbackStageKey = currentStageKey;
      setCurrentStageKey(stageKey);
      setIsUpdating(true);
      try {
        await updateClaimStage(claimId, stageKey);
        fallbackStageKey = stageKey;
        if (onUpdate) {
          await onUpdate(stageKey);
        }
      } catch (error) {
        reportAxiosError(error);
        setCurrentStageKey(fallbackStageKey);
      } finally {
        setIsUpdating(false);
      }
    },
    [claimId, onUpdate, currentStageKey]
  );

  return (
    <EditableChip
      value={currentStageKey}
      options={claimStageOptions}
      onChange={handleStageChange}
      className={cn('claim-stage--selected group max-w-none', { 'claim-stage--view-only w-max': viewOnly })}
      disabled={isUpdating}
      viewOnly={viewOnly}
    />
  );
};

export default ClaimStageViewAndUpdate;
